import React, {useEffect, useState} from 'react';
import {MaterialReactTable} from 'material-react-table';
import {MRT_Localization_RU} from 'material-react-table/locales/ru';
import {Box, Tooltip, Typography} from '@mui/material';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {styled} from '@mui/material/styles';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {getMailingHistory} from '../../http/mailingAPI';
import useSnackbar from '../../hooks/useSnackbar';

dayjs.extend(utc);
dayjs.extend(timezone);

const TableContainer = styled(Box)(() => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
}));

const MailingHistory = () => {
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 20,
    });
    const [sorting, setSorting] = useState([{id: 'date', desc: true}]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');

    const snack = useSnackbar(false, 'Ошибка');

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const query = {
                page: pagination.pageIndex + 1,
                limit: pagination.pageSize,
                sortBy: sorting[0]?.id,
                order: sorting[0]?.desc ? 'desc' : 'asc',
                search: globalFilter,
                ...Object.fromEntries(
                    columnFilters.map(({id, value}) => [id, value])
                ),
            };
            const response = await getMailingHistory(query);
            setData(response.data || []);
            setTotal(response.total || 0);
        } catch (error) {
            console.error('Ошибка загрузки истории:', error);
            snack.handleChange(true, 'Ошибка загрузки истории');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [pagination, sorting, columnFilters, globalFilter]);

    const columns = [
        {
            accessorKey: 'title',
            header: 'Название',
            size: 180,
        },
        {
            accessorKey: 'recipientPhone',
            header: 'Получатель',
            size: 130,
        },
        {
            accessorKey: 'author.name',
            header: 'Автор',
            Cell: ({row}) => row.original.author?.internalName
                || row.original.author?.name
                || row.original.authorInternalNameSnapshot
                || row.original.authorNameSnapshot
                || 'Неизвестный',
            size: 120,
        },
        {
            accessorKey: 'message',
            header: 'Сообщение',
            Cell: ({row}) => {
                const msg = row.original.message;
                const truncated = msg.length > 80 ? `${msg.substring(0, 80)}...` : msg;
                return (
                    <Tooltip
                        title={
                            <Typography
                                sx={{
                                    whiteSpace: 'pre-wrap',
                                    maxWidth: 400,
                                    fontSize: '0.875rem'
                                }}
                            >
                                {msg}
                            </Typography>
                        }
                        arrow
                        placement="top-start"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    maxWidth: 500,
                                }
                            }
                        }}
                    >
                        <span style={{cursor: 'pointer'}}>{truncated}</span>
                    </Tooltip>
                );
            },
            size: 250,
        },
        {
            accessorKey: 'date',
            header: 'Дата',
            accessorFn: (row) => new Date(row.date),
            filterVariant: 'date-range',
            Cell: ({cell}) => dayjs(cell.getValue()).format('DD.MM.YYYY HH:mm'),
            size: 140,
        },
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ height: 'calc(100vh - 260px)', minHeight: 360, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <TableContainer>
                    <Typography variant="h4" component="h1" sx={{mb: 2, m: 0}}>
                        История рассылок (всего: {total})
                    </Typography>

                    <MaterialReactTable
                        columns={columns}
                        data={data}
                        localization={MRT_Localization_RU}
                        enableStickyHeader
                        enableFullScreenToggle={false}
                        enableDensityToggle={false}
                        manualPagination
                        manualSorting
                        manualFiltering
                        rowCount={total}
                        state={{
                            isLoading,
                            pagination,
                            sorting,
                            columnFilters,
                            globalFilter,
                        }}
                        onPaginationChange={setPagination}
                        onSortingChange={setSorting}
                        onColumnFiltersChange={setColumnFilters}
                        onGlobalFilterChange={setGlobalFilter}
                        initialState={{
                            density: 'compact',
                            showColumnFilters: true,
                        }}
                        muiTablePaperProps={{
                            elevation: 0,
                            sx: {boxShadow: 'none', display: 'flex', flexDirection: 'column', height: '100%'},
                        }}
                        muiTableContainerProps={{
                            sx: {flex: 1, minHeight: 0, overflow: 'auto'},
                        }}
                    />
                </TableContainer>
            </Box>
        </LocalizationProvider>
    );
};

export default MailingHistory;
